import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white grid lg:grid-cols-2">
      <div className="hidden lg:flex relative grid-bg-brand p-12 flex-col justify-between border-r border-[#1c1c1c]">
        <div className="font-heading text-lg font-bold uppercase tracking-tight">Chongie Aviation</div>
        <div className="max-w-md">
          <div className="label-eyebrow mb-3" style={{ color: "#8a83d4" }}>SECURE RECOVERY</div>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight leading-tight">
            Forgot your<br />password?
          </h1>
          <p className="mt-6 text-sm text-[#b9b2e8] max-w-sm">
            Enter the email registered with your Chongie Aviation account and we'll send a one-time link to set a new password.
          </p>
        </div>
        <div className="font-mono text-xs text-[#666]">Link valid for 2 hours</div>
      </div>

      <div className="relative flex items-center justify-center p-8 lg:p-16 bg-brand-vertical">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <form onSubmit={submit} className="relative w-full max-w-md space-y-6" data-testid="forgot-password-form">
          <Link to="/login" className="inline-flex items-center text-xs font-mono text-[#888] hover:text-white">
            <ArrowLeft className="h-3 w-3 mr-1" /> Back to sign in
          </Link>
          <div>
            <div className="label-eyebrow">ACCOUNT RECOVERY</div>
            <h2 className="font-heading text-3xl uppercase font-bold mt-2">Reset password</h2>
          </div>

          {sent ? (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-5">
              <div className="font-heading uppercase tracking-wider text-emerald-300">Check your inbox</div>
              <p className="text-sm text-[#bbb] mt-2">
                If <span className="font-mono">{email}</span> matches an active account, a reset link has been sent. It expires in 2 hours.
              </p>
              <p className="text-xs font-mono text-[#666] mt-3">Didn't get it? Check spam, or contact your admin.</p>
            </div>
          ) : (
            <>
              <div>
                <Label className="label-eyebrow">EMAIL</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
                  <Input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                    className="pl-10 bg-black border-[#222] rounded-none h-12 font-mono text-sm"
                    placeholder="you@chongieaviation.ie"
                  />
                </div>
              </div>
              <Button
                type="submit" disabled={busy}
                data-testid="forgot-submit"
                className="w-full h-12 rounded-none btn-brand border border-[#3b2bd0] font-heading font-bold uppercase tracking-[0.15em]"
              >
                <Send className="h-4 w-4 mr-2" />
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
